import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import FilterPanel from './components/FilterPanel';
import Timeline from './components/Timeline';
import type { FilterState } from './types';
import { triggerManualIngest } from './api/client';

export default function App() {
  const { instance, accounts } = useMsal();
  const [filters, setFilters] = useState<FilterState>({});
  const [ingestMailbox, setIngestMailbox] = useState('john.kahrhoff@ibewlocal1.org');
  const [ingestFolder, setIngestFolder] = useState('');
  const [ingestStatus, setIngestStatus] = useState('');

  const userEmail = accounts[0]?.username ?? '';

  function handleLogout() {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  async function handleManualIngest() {
    if (!ingestMailbox) return;
    const folderLabel = ingestFolder.trim() ? ` (folder: ${ingestFolder.trim()})` : '';
    setIngestStatus(`Ingesting${folderLabel}…`);
    try {
      const result = await triggerManualIngest(ingestMailbox, ingestFolder.trim() || undefined);
      setIngestStatus(`Done: ${result.ingested} ingested, ${result.errors} errors${folderLabel}`);
    } catch (e: any) {
      setIngestStatus(`Error: ${e?.response?.data?.error?.message || e.message}`);
    }
  }

  return (
    <div style={styles.app}>
      {/* ── Top bar ── */}
      <header style={styles.header}>
        <h1 style={styles.logo}>📬 Outlook Audit</h1>
        <div style={styles.ingestBar}>
          <input
            style={styles.ingestInput}
            placeholder="Mailbox (user@contoso.com)"
            value={ingestMailbox}
            onChange={(e) => setIngestMailbox(e.target.value)}
          />
          <input
            style={styles.folderInput}
            placeholder="Ingest folder only (e.g. PW)"
            value={ingestFolder}
            onChange={(e) => setIngestFolder(e.target.value)}
          />
          <button style={styles.ingestBtn} onClick={handleManualIngest}>
            Sync
          </button>
          {ingestStatus && <span style={styles.ingestStatus}>{ingestStatus}</span>}
        </div>
        <div style={styles.userBar}>
          {userEmail && <span style={styles.userEmail}>{userEmail}</span>}
          <button style={styles.logoutBtn} onClick={handleLogout}>Sign out</button>
        </div>
      </header>

      {/* ── Main layout ── */}
      <div style={styles.main}>
        <FilterPanel filters={filters} onChange={setFilters} />
        <Timeline filters={filters} />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  app: { display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: 'system-ui, sans-serif' },
  header: {
    display: 'flex', alignItems: 'center', gap: 16,
    padding: '12px 20px', background: '#1e3a5f', color: '#fff',
    flexShrink: 0,
  },
  logo: { fontSize: 18, fontWeight: 700, margin: 0 },
  ingestBar: { display: 'flex', alignItems: 'center', gap: 8, flex: 1 },
  ingestInput: {
    padding: '6px 10px', borderRadius: 6, border: 'none',
    fontSize: 13, width: 240,
  },
  folderInput: {
    padding: '6px 10px', borderRadius: 6, border: 'none',
    fontSize: 13, width: 160,
  },
  ingestBtn: {
    padding: '6px 16px', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
  },
  ingestStatus: { fontSize: 13, color: '#93c5fd' },
  userBar: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  userEmail: { fontSize: 13, color: '#93c5fd' },
  logoutBtn: {
    padding: '5px 12px', background: 'transparent', color: '#93c5fd',
    border: '1px solid #93c5fd', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  main: { display: 'flex', flex: 1, overflow: 'hidden' },
};
